import type { ReactNode } from 'react';
import {
  PROJECT_TYPES,
  PROJECT_TYPE_DISPLAY,
  getProjectTypeColor,
  type ProjectTypeValue,
} from '@/utils/projectTypeColors';
import { ProjectTypeSymbol } from '@/utils/ProjectTypeSymbol';
import './ProjectTypeGlyph.css';

export function resolveProjectTypeValue(type: string | null | undefined): ProjectTypeValue | null {
  if (!type?.trim()) return null;
  const key = type.trim();
  if (PROJECT_TYPES.includes(key as ProjectTypeValue)) return key as ProjectTypeValue;
  return null;
}

interface ProjectTypeGlyphProps {
  type: string | null | undefined;
  /** Множитель к размерам из PROJECT_TYPE_DISPLAY (кнопка таблицы = 1) */
  scale?: number;
  className?: string;
  /** Если тип неизвестен — показать Common (`•`). Иначе null */
  fallbackToCommon?: boolean;
}

/** SVG-глиф для кнопки типа в таблице проектов. */
export function ProjectTypeGlyph({
  type,
  scale = 1,
  className,
  fallbackToCommon = false,
}: ProjectTypeGlyphProps) {
  let typeKey = resolveProjectTypeValue(type);
  if (!typeKey) {
    if (!fallbackToCommon) return null;
    typeKey = 'Common';
  }

  const config = PROJECT_TYPE_DISPLAY[typeKey];
  const color = getProjectTypeColor(typeKey);
  const viewW = Math.max(1, Math.round(config.typeBtnView.w * scale));
  const viewH = Math.max(1, Math.round(config.typeBtnView.h * scale));
  const fontPx = Math.max(1, parseFloat(config.fontSize) * scale);
  const strokeWidth = Math.max(0.4, 1.5 * scale);
  const text = typeof config.label === 'string' ? config.label : null;
  const Icon = typeof config.label === 'string' ? null : config.label;
  /** Иконка — 0.8 размера шрифта: на глаз как высота заглавной буквы соседних типов */
  const iconPx = fontPx * 0.8;

  return (
    <svg
      className={`project-type-glyph${className ? ` ${className}` : ''}`}
      width={viewW}
      height={viewH}
      viewBox={`0 0 ${viewW} ${viewH}`}
      aria-hidden
    >
      {Icon ? (
        <Icon x={(viewW - iconPx) / 2} y={(viewH - iconPx) / 2} size={iconPx} color={color} strokeWidth={2.25} />
      ) : (
        <text
          className="project-type-glyph__text"
          x={viewW / 2}
          y={viewH / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fill={color}
          strokeWidth={strokeWidth}
          style={{
            fontSize: `${fontPx}px`,
            fontWeight: 700,
            fontFamily: 'var(--font-sans, system-ui, sans-serif)',
            ['--type-glyph-fill' as string]: color,
          }}
        >
          {text}
        </text>
      )}
    </svg>
  );
}

interface ProjectNameWithTypeGlyphProps {
  type: string | null | undefined;
  children: ReactNode;
  className?: string;
}

/**
 * Название проекта с буквой/символом типа в потоке текста
 * (как бейдж типа перед именем в колонке «Название» таблицы проектов).
 */
export function ProjectNameWithTypeGlyph({
  type,
  children,
  className,
}: ProjectNameWithTypeGlyphProps) {
  const typeKey = resolveProjectTypeValue(type);
  if (!typeKey) {
    return <>{children}</>;
  }

  const config = PROJECT_TYPE_DISPLAY[typeKey];
  const color = getProjectTypeColor(typeKey);
  const isDot = typeKey === 'Common';

  return (
    <span className={`project-name-with-type-glyph${className ? ` ${className}` : ''}`}>
      <span
        className={`project-name-with-type-glyph__mark${isDot ? ' project-name-with-type-glyph__mark_dot' : ''}`}
        style={{ color }}
        aria-hidden
      >
        <ProjectTypeSymbol symbol={config.label} />
      </span>
      <span className="project-name-with-type-glyph__name">{children}</span>
    </span>
  );
}
